<div class="actions-container">
    <mat-card-actions class="detail-card-actions">
        @if (!isEditable) {
            <button (click)="edit()"
                    [disabled]="isEditDisabled!"
                    color="primary"
                    data-test="edit-button"
                    mat-icon-button>
                <mat-icon>edit</mat-icon>
            </button>
        }
        @if (isEditable && assetId && assetValuationId && scenario?.id) {
            <button (click)="quickForecastToggle.emit()"
                    [matTooltip]="quickForecastOpen ? 'Close QuickForecast' : 'Open QuickForecast'"
                    aria-label="Toggle QuickForecast"
                    color="primary"
                    data-test="quick-forecast-toggle"
                    mat-icon-button
                    type="button">
                <mat-icon>functions</mat-icon>
            </button>
        }
        @if (isEditable) {
            <button (click)="save()"
                    [disabled]="exitYearEquityIsNegative"
                    color="primary"
                    data-test="save-button"
                    mat-icon-button>
                <mat-icon>save</mat-icon>
            </button>
        }
        @if (isEditable) {
            <button (click)="cancel()"
                    color="primary"
                    data-test="cancel-button"
                    mat-icon-button>
                <mat-icon>cancel</mat-icon>
            </button>
        }
        @if (scenario?.scenario?.code; as type) {
            @if (!isEditable) {
                <button [matMenuTriggerFor]="copyFOVFinancialPerformanceRecords" aria-label="More options" mat-icon-button (click)="$event.stopPropagation()">
                    <mat-icon>more_vert</mat-icon>
                </button>
            }
            <mat-menu #copyFOVFinancialPerformanceRecords="matMenu">
                @if (type !== 'LOW' && !isEditable) {
                    <button mat-menu-item (click)="copy(type, 'LOW')">
                        <mat-icon>content_copy</mat-icon>
                        <span>Copy from Low</span>
                    </button>
                }
                @if (type !== 'BASE' && !isEditable) {
                    <button mat-menu-item (click)="copy(type, 'BASE')">
                        <mat-icon>content_copy</mat-icon>
                        <span>Copy from Base</span>
                    </button>
                }
                @if (type !== 'HIGH' && !isEditable) {
                    <button mat-menu-item (click)="copy(type, 'HIGH')">
                        <mat-icon>content_copy</mat-icon>
                        <span>Copy from High</span>
                    </button>
                }
            </mat-menu>
        }
    </mat-card-actions>
</div>

<mat-card-content class="detail-card-content">
    @if (exitYearEquityIsNegative && isEditable) {
        <valumize-info-panel
            [appearance]="'ERROR'"
            [message]="'Exit-year equity would be negative. Reduce Net Debt or increase EV/EBITDA multiple.'">
        </valumize-info-panel>
    }
    @if (showNonExitEquityWarning) {
        <valumize-info-panel
            [appearance]="'WARNING'"
            [message]="nonExitEquityWarningMessage"
            [closable]="true"
            (closePanel)="dismissNonExitWarning()">
        </valumize-info-panel>
    }
    <section class="table-container" tabindex="0" (paste)="onPaste($event)" (keydown)="onGridKeydown($event)">
        @if (selectedAssetFinancialPerformanceData.length === 0 && !isEditable) {
            <div class="empty-state">
                No financial performance records available. Start editing to add a new one.
            </div>
        }

        @if (selectedAssetFinancialPerformanceData.length > 0) {
            <div class="table-wrapper">
                <table mat-table [dataSource]="fieldNames">
                    <ng-container matColumnDef="rowName" sticky>
                        <th mat-header-cell *matHeaderCellDef>
                            <div class="fye-header">
                                <span>FYE {{ this.fiscalYearEndMonth | codeFormat: codeTableFiscalYearEnd | async }}</span>
                                @if (companyCurrencyIso) {
                                    <valumize-currency-badge
                                        kind="COMPANY"
                                        [iso]="companyCurrencyIso"
                                        size="compact"
                                        tooltip="Financial performance figures (Revenue, EBITDA, etc.) are in the company's reporting currency">
                                    </valumize-currency-badge>
                                }
                            </div>
                        </th>
                        <td mat-cell *matCellDef="let fieldName">
                            @if (isWalkField(fieldName)) {
                                @switch (fieldName) {
                                    @case ('walkOwnership') {
                                        <span class="walk-label">% ownership</span>
                                    }
                                    @case ('walkFundEquity') {
                                        <span class="walk-label">Fund Equity (Company CCY)</span>
                                        @if (companyCurrencyIso) {
                                            <span class="ccy-tag company">{{ companyCurrencyIso }}</span>
                                        }
                                    }
                                    @case ('walkFx') {
                                        <span class="walk-label">Company &lt;&gt; Fund FX</span>
                                        @if (companyCurrencyIso && fundCurrencyIso) {
                                            <span class="ccy-tag fx">{{ companyCurrencyIso }}&nbsp;&#10231;&nbsp;{{ fundCurrencyIso }}</span>
                                        }
                                        @if (scenario?.walkToProceeds?.fxRateFund?.modDate; as fxDate) {
                                            <span class="fx-date">as of {{ fxDate | date:'dd/MM/yyyy' }}</span>
                                        }
                                    }
                                    @case ('walkFundProceeds') {
                                        <span class="walk-label">Fund Proceeds</span>
                                        @if (fundCurrencyIso) {
                                            <span class="ccy-tag fund">{{ fundCurrencyIso }}</span>
                                        }
                                    }
                                }
                            } @else {
                                {{ fieldDisplayNames[fieldName] || fieldName }}
                            }
                        </td>
                    </ng-container>
                    <ng-container matColumnDef="historyBase">
                        <th mat-header-cell *matHeaderCellDef class="historyBaseRecord">
                            @if (historyBase) {
                                <span>{{ historyBaseLabel }}</span>
                                @if (isCustomHistoryBase && historyBase.period.keyDate) {
                                    <span class="history-base-date">{{ historyBase.period!.keyDate | date:'d MMM yyyy' }}</span>
                                }
                                @if (historyBase.stale) {
                                    <mat-icon color="warn" class="stale-warning-icon"
                                              matTooltip="Source record has changed since this copy was made.">warning
                                    </mat-icon>
                                }
                            }
                        </th>
                        <td mat-cell *matCellDef="let fieldName" class="historyBaseRecord">
                            @if (!isWalkField(fieldName)) {
                                @if (isEditable && isCustomHistoryBase && isHistoryBaseEditableField(fieldName)) {
                                    <input class="input-field input-number-narrow" type="number"
                                           [formControl]="getHistoryBaseFormControl(fieldName)">
                                } @else {
                                    <span>{{ getHistoryBaseFieldValue(fieldName) | traceableFormat }}</span>
                                }
                            }
                        </td>
                    </ng-container>
                    @for (record of recordsForTable; track record.period.keyDate + '-' + record.period.type + '-' + record.forecast; ) {
                        <ng-container
                            matColumnDef="{{record.period.keyDate}}-{{record.period.type}}-{{record.forecast ? 'Forecast' : 'History'}}">
                            <th mat-header-cell
                                [ngClass]="{
                            'forecastRecord': record.forecast,
                            'exitYearForecastRecord': isExitRecord(record) && isPeriodValid(record.period.keyDate, record.period.type),
                            'invalidRecord': !isPeriodValid(record.period.keyDate, record.period.type),
                            'zeroEquityYear': isZeroEquityYear(record)
                        }"
                                *matHeaderCellDef>
                                <div class="period-header">
                                    <div>{{ record.period | traceableFormat }}</div>
                                    @if (!isPeriodValid(record.period.keyDate, record.period.type)) {
                                        <div>&nbsp;
                                            <mat-icon
                                                [matTooltip]="'The period end date ' + formatDate(record.period.keyDate) + ' is inconsistent with the current fiscal year end month.'">
                                                error_outlined
                                            </mat-icon>
                                        </div>
                                    }
                                    @if (isDeletableForecastRecord(record)) {
                                        <button (click)="deleteForecastRecord(record)" color="primary" mat-icon-button>
                                            <mat-icon>delete</mat-icon>
                                        </button>
                                    }
                                </div>
                            </th>
                            @if (record.forecast) {
                                <td mat-cell *matCellDef="let fieldName"
                                    [ngClass]="{
                                'forecastRecord': record.forecast,
                                'exitYearForecastRecord': isExitRecord(record) && isPeriodValid(record.period.keyDate, record.period.type),
                                'invalidRecord': !isPeriodValid(record.period.keyDate, record.period.type),
                                'zeroEquityYear': isZeroEquityYear(record)
                            }">
                                    @if (isWalkField(fieldName)) {
                                        @if (isExitRecord(record)) {
                                            @if (fieldName === 'walkFx') {
                                                <span>{{ scenario?.walkToProceeds?.fxRateFund?.fraction | decimalFormat: 'number' }}</span>
                                            } @else {
                                                <span>{{ getWalkFieldValue(fieldName) | traceableFormat }}</span>
                                            }
                                        }
                                    } @else {
                                        @if (!isEditable) {
                                            <span>{{ getFieldValue(fieldName, record.period.keyDate, record.period.type, record.forecast) | traceableFormat }}</span>
                                        }
                                        @if (isEditable) {
                                            @if (isFieldEditable(fieldName) && isPeriodValid(record.period.keyDate, record.period.type)) {
                                                <input class="input-field input-number-narrow" type="text"
                                                       [formControl]="getFormControl(record.period.keyDate, record.period.type, fieldName)"
                                                       [attr.data-field]="fieldName"
                                                       [attr.data-col]="record.period.keyDate + '-' + record.period.type"
                                                       (change)="calc()">
                                            } @else {
                                                <span>{{ getFieldValue(fieldName, record.period.keyDate, record.period.type, record.forecast) | traceableFormat }}</span>
                                            }
                                        }
                                    }
                                </td>
                            } @else {
                                <td mat-cell *matCellDef="let fieldName"
                                    [ngClass]="{'invalidRecord': !isPeriodValid(record.period.keyDate, record.period.type), 'zeroEquityYear': isZeroEquityYear(record)}">
                                    @if (!isWalkField(fieldName)) {
                                        <span>{{ getFieldValue(fieldName, record.period.keyDate, record.period.type, record.forecast) | traceableFormat }}</span>
                                    }
                                </td>
                            }
                        </ng-container>
                    }
                    <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                    <tr mat-row *matRowDef="let fieldName; columns: displayedColumns;" [class]="styleRowImportant(fieldName)"></tr>
                </table>
                @if (isEditable) {
                    <button class="add-button" (click)="addForecastRecord()" mat-fab>
                        <mat-icon>add</mat-icon>
                    </button>
                }
            </div>
        }
    </section>

    <div class="history-count">
        <label for="historyCount">History Records:</label>
        <input id="historyCount" class="input-number-narrow" type="number" [formControl]="historyCount" min="0" [max]="maxHistoryRecords"/>
        @if (isEditable) {
            <mat-slide-toggle
                [checked]="inputMode === 'ABSOLUTE'"
                (change)="onInputModeToggle($event.checked)"
                data-test="input-mode-toggle">
                Absolute Values
            </mat-slide-toggle>
            @if (financialDataForms?.invalid) {
                <span class="validation-error">A value exceeds the maximum allowed (190'000 Mio).</span>
            }
        }
    </div>
</mat-card-content>
